import { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wrench, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PublicFooter from "@/components/PublicFooter";

export default function Login() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("dumitrusarbul@gmail.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [demoHint, setDemoHint] = useState(null);

  useEffect(() => {
    api.get("/auth/demo-hint")
      .then(({ data }) => { if (data?.is_demo) setDemoHint(data); })
      .catch(() => setDemoHint(null));
  }, []);

  if (status === "authed") {
    const from = location.state?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Welcome back");
    // Phase 64 — first-manager / fresh-tenant flow: if the tenant hasn't
    // completed onboarding yet, route them straight to /onboarding so the
    // wizard fires instead of dropping them into an empty calendar.
    const from = location.state?.from?.pathname || "/";
    let destination = from;
    if (res.manager && res.manager.onboarding_completed === false) {
      destination = "/onboarding";
    }
    navigate(destination, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex bg-primary text-primary-foreground relative overflow-hidden">
        <div className="seam absolute top-0 inset-x-0" />
        <div className="p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-accent text-accent-foreground rounded-[6px]">
              <Wrench className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xl font-extrabold tracking-tight">JobsBook</div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary-foreground/60">
                Bookings & invoicing for service businesses
              </div>
            </div>
          </div>

          <div className="space-y-6 max-w-md">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manager console</div>
            <h1 className="text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight">
              Bookings & invoicing
              <span className="text-accent"> for service businesses.</span>
            </h1>
            <p className="text-primary-foreground/70 text-base leading-relaxed">
              Manager console. Sign in to manage customers, staff, job templates and invoice settings.
            </p>
          </div>

          <div className="flex items-center gap-6 text-xs text-primary-foreground/50 mono uppercase tracking-widest">
            <span>Bookings</span>
            <span>·</span>
            <span>Invoicing</span>
            <span>·</span>
            <span>Multi-currency</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md space-y-6 bg-card border border-border rounded-[8px] p-8 shadow-sm"
          data-testid="login-form"
        >
          <div className="space-y-1">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manager Login</div>
            <h2 className="text-2xl font-extrabold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground">Use your manager credentials to continue.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="h-11"
            />
          </div>

          {error && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-[6px] px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 btn-cta"
            data-testid="login-submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>

          {demoHint && (
            <button
              type="button"
              onClick={() => { setEmail(demoHint.email); setPassword(demoHint.password); }}
              className="w-full text-left bg-accent/10 hover:bg-accent/15 border border-accent/30 rounded-[6px] px-3 py-2 transition-colors"
              data-testid="login-demo-hint"
            >
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-accent">
                <Sparkles className="h-3 w-3" /> Demo install · click to autofill
              </div>
              <div className="mt-1 mono text-xs text-foreground/80 break-all">
                {demoHint.email} <span className="text-muted-foreground">·</span> {demoHint.password}
              </div>
            </button>
          )}

          <div className="text-[11px] text-muted-foreground mono uppercase tracking-widest text-center pt-2 border-t border-border">
            Bookings & invoicing for service businesses
          </div>
          <div className="pt-3">
            <PublicFooter tone="dark" />
          </div>
        </form>
      </div>
    </div>
  );
}
